import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileText, FileDown, ExternalLink, AlertTriangle, Filter, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { supplierNcService, supplierService } from '../services/api';

const SEV_CLR = { Bassa: 'bg-slate-400 text-white', Media: 'bg-amber-500 text-white', Alta: 'bg-orange-600 text-white', Critica: 'bg-red-600 text-white' };
const STATUS_CLR = {
  Bozza: 'bg-slate-300 text-slate-800', Emessa: 'bg-sky-500 text-white',
  'In attesa di risposta': 'bg-amber-500 text-white', 'In analisi fornitore': 'bg-blue-500 text-white',
  'Azione correttiva ricevuta': 'bg-violet-500 text-white', Accettata: 'bg-emerald-600 text-white',
  Rifiutata: 'bg-red-600 text-white', Chiusa: 'bg-slate-500 text-white',
};

const emptyFilters = { supplier_id: '', status: '', severity: '', defect_category_id: '', from_date: '', to_date: '', search: '', open_only: false, overdue_only: false, with_debit_note: false };
const emptyForm = () => ({ supplier_id: '', defect_category_id: '', issue_date: new Date().toISOString().slice(0,10), response_deadline: '', severity: 'Media', status: 'Emessa', defect_description: '', requested_action: '', quantity_involved: '', supplier_ddt_number: '', raw_part_code: '' });

export default function SupplierNcsPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [ncs, setNcs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [enums, setEnums] = useState({ statuses: [], severities: [], defect_categories: [] });
  const [filters, setFilters] = useState({ ...emptyFilters, open_only: sp.get('open_only')==='true', overdue_only: sp.get('overdue_only')==='true' });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k,v]) => { if (v !== '' && v !== false && v != null) params[k] = v; });
      const res = await supplierNcService.getAll(params);
      setNcs(res.data);
    } catch { toast.error('Errore caricamento NC fornitori'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [sRes, eRes] = await Promise.all([supplierService.getAll(), supplierNcService.enums()]);
        setSuppliers(sRes.data || []); setEnums(eRes.data || {});
      } catch (e) { console.error('SupplierNcs: load suppliers/enums failed', e); }
    })();
  }, []);

  const openReport = (id) => window.open(supplierNcService.reportPdfUrl(id), '_blank');
  const openDossier = (id) => window.open(supplierNcService.dossierPdfUrl(id), '_blank');

  const handleCreate = async () => {
    if (!form.supplier_id || !form.defect_category_id || !form.defect_description || !form.requested_action || !form.response_deadline) {
      toast.error('Compila i campi obbligatori'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, quantity_involved: form.quantity_involved ? Number(form.quantity_involved) : null };
      const res = await supplierNcService.create(payload);
      toast.success(`Creata ${res.data.supplier_nc_code}`);
      setCreateOpen(false); setForm(emptyForm());
      navigate(`/supplier-ncs/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione'); }
    finally { setSaving(false); }
  };

  const today = new Date().toISOString().slice(0,10);

  return (
    <div className="space-y-6" data-testid="supplier-ncs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><AlertTriangle className="w-6 h-6 text-orange-600" />Non Conformità Fornitori</h1>
          <p className="text-sm text-slate-500 mt-1">NC emesse a fornitori con azione correttiva, note di debito e valutazione annuale</p>
        </div>
        <div className="flex gap-2">
          <Link to="/supplier-ncs/evaluation-dashboard"><Button variant="outline" data-testid="dashboard-btn"><BarChart3 className="w-4 h-4 mr-1" />Dashboard Valutazione</Button></Link>
          <Button onClick={() => { setForm(emptyForm()); setCreateOpen(true); }} data-testid="new-sncf-btn"><Plus className="w-4 h-4 mr-1" />Nuova NC Fornitore</Button>
        </div>
      </div>

      <Card><CardContent className="pt-5">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 space-y-1"><Label className="text-xs">Ricerca</Label><Input value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} placeholder="Cerca..." data-testid="filter-search" /></div>
          <div className="space-y-1"><Label className="text-xs">Fornitore</Label>
            <Select value={filters.supplier_id || 'all'} onValueChange={v=>setFilters(f=>({...f,supplier_id:v==='all'?'':v}))}>
              <SelectTrigger data-testid="filter-supplier"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Stato</Label>
            <Select value={filters.status || 'all'} onValueChange={v=>setFilters(f=>({...f,status:v==='all'?'':v}))}>
              <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{(enums.statuses||[]).map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Gravità</Label>
            <Select value={filters.severity || 'all'} onValueChange={v=>setFilters(f=>({...f,severity:v==='all'?'':v}))}>
              <SelectTrigger><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{(enums.severities||[]).map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Categoria</Label>
            <Select value={filters.defect_category_id || 'all'} onValueChange={v=>setFilters(f=>({...f,defect_category_id:v==='all'?'':v}))}>
              <SelectTrigger><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{(enums.defect_categories||[]).map(c=><SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={filters.from_date} onChange={e=>setFilters(f=>({...f,from_date:e.target.value}))} /></div>
          <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={filters.to_date} onChange={e=>setFilters(f=>({...f,to_date:e.target.value}))} /></div>
          <div className="flex items-end gap-3 md:col-span-3">
            <label className="flex items-center gap-1 text-sm cursor-pointer"><Checkbox checked={filters.open_only} onCheckedChange={v=>setFilters(f=>({...f,open_only:!!v}))} data-testid="filter-open"/> Solo aperte</label>
            <label className="flex items-center gap-1 text-sm cursor-pointer"><Checkbox checked={filters.overdue_only} onCheckedChange={v=>setFilters(f=>({...f,overdue_only:!!v}))} /> Solo scadute</label>
            <label className="flex items-center gap-1 text-sm cursor-pointer"><Checkbox checked={filters.with_debit_note} onCheckedChange={v=>setFilters(f=>({...f,with_debit_note:!!v}))} /> Con nota di debito</label>
            <Button variant="ghost" size="sm" onClick={()=>setFilters(emptyFilters)}><Filter className="w-4 h-4 mr-1"/>Reset</Button>
          </div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="pt-4">
        {loading ? <div className="text-center py-8"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"/></div>
        : ncs.length===0 ? <p className="text-center text-slate-500 py-10">Nessuna NC fornitore trovata</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50">
                {['Codice','Data','Fornitore','Prodotto / Grezzo','Categoria','Q.tà','Gravità','Stato','Scadenza','Nota Debito','Azioni'].map(h=><th key={h} className="px-3 py-2 text-left font-medium text-slate-600">{h}</th>)}
              </tr></thead>
              <tbody>
                {ncs.map(nc => {
                  const overdue = nc.response_deadline && nc.response_deadline < today && nc.status !== 'Chiusa';
                  return (
                    <tr key={nc.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`sncf-row-${nc.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold"><Link to={`/supplier-ncs/${nc.id}`} className="text-sky-700 hover:underline">{nc.supplier_nc_code}</Link></td>
                      <td className="px-3 py-2 font-mono text-xs">{nc.issue_date}</td>
                      <td className="px-3 py-2">{nc.supplier_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{nc.product_code || nc.raw_part_code || '—'}</td>
                      <td className="px-3 py-2 text-xs">{nc.defect_category_name || '—'}</td>
                      <td className="px-3 py-2 text-center">{nc.quantity_involved ?? '—'}</td>
                      <td className="px-3 py-2"><Badge className={`${SEV_CLR[nc.severity]||'bg-slate-400 text-white'} text-xs px-2 py-0.5`}>{nc.severity}</Badge></td>
                      <td className="px-3 py-2"><Badge className={`${STATUS_CLR[nc.status]||''} text-xs px-2 py-0.5`}>{nc.status}</Badge></td>
                      <td className={`px-3 py-2 font-mono text-xs ${overdue ? 'text-red-600 font-bold':''}`}>{nc.response_deadline || '—'}{overdue && <span className="ml-1">⚠</span>}</td>
                      <td className="px-3 py-2 text-xs">{nc.debit_note_amount ? <span className="font-semibold text-violet-700">€{nc.debit_note_amount.toFixed(2)}</span> : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={()=>navigate(`/supplier-ncs/${nc.id}`)}><ExternalLink className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="sm" onClick={()=>openReport(nc.id)}><FileText className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="sm" onClick={()=>openDossier(nc.id)}><FileDown className="w-4 h-4"/></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuova NC Fornitore</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1"><Label>Fornitore *</Label>
              <Select value={form.supplier_id} onValueChange={v=>setForm(f=>({...f,supplier_id:v}))}>
                <SelectTrigger data-testid="form-supplier"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                <SelectContent>{suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Categoria difetto *</Label>
              <Select value={form.defect_category_id} onValueChange={v=>setForm(f=>({...f,defect_category_id:v}))}>
                <SelectTrigger data-testid="form-category"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                <SelectContent>{(enums.defect_categories||[]).map(c=><SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Data emissione *</Label><Input type="date" value={form.issue_date} onChange={e=>setForm(f=>({...f,issue_date:e.target.value}))}/></div>
            <div className="space-y-1"><Label>Scadenza risposta *</Label><Input type="date" value={form.response_deadline} onChange={e=>setForm(f=>({...f,response_deadline:e.target.value}))}/></div>
            <div className="space-y-1"><Label>Gravità *</Label>
              <Select value={form.severity} onValueChange={v=>setForm(f=>({...f,severity:v}))}>
                <SelectTrigger data-testid="form-severity"><SelectValue/></SelectTrigger>
                <SelectContent>{(enums.severities||[]).map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Q.tà coinvolta</Label><Input type="number" min="0" value={form.quantity_involved} onChange={e=>setForm(f=>({...f,quantity_involved:e.target.value}))}/></div>
            <div className="space-y-1"><Label>DDT fornitore</Label><Input value={form.supplier_ddt_number} onChange={e=>setForm(f=>({...f,supplier_ddt_number:e.target.value}))}/></div>
            <div className="space-y-1"><Label>Codice grezzo</Label><Input value={form.raw_part_code} onChange={e=>setForm(f=>({...f,raw_part_code:e.target.value}))}/></div>
            <div className="col-span-2 space-y-1"><Label>Descrizione difetto *</Label><Textarea rows={2} value={form.defect_description} onChange={e=>setForm(f=>({...f,defect_description:e.target.value}))}/></div>
            <div className="col-span-2 space-y-1"><Label>Azione richiesta *</Label><Textarea rows={2} value={form.requested_action} onChange={e=>setForm(f=>({...f,requested_action:e.target.value}))}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)} disabled={saving}>Annulla</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="form-submit">{saving ? 'Creo...' : 'Crea NC'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
